import { useState, useEffect, useRef } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import {
  Button,
  Input,
  Label,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Calendar,
  Textarea,
} from '@pantry/ui';
import { cn } from '@pantry/ui';
import {
  EXPIRY_PRESETS,
  QUANTITY_PRESETS,
  calculateExpiryDate,
  parseLocalDate,
  parseDateInput,
} from '@pantry/shared';
import { CalendarIcon } from '@heroicons/react/24/outline';
import { format } from 'date-fns';

interface InstanceFieldsSectionProps {
  quantity: number;
  onQuantityChange: (q: number) => void;
  purchaseDate: Date | undefined;
  onPurchaseDateChange: (d: Date | undefined) => void;
  expiryDate: Date | undefined;
  onExpiryDateChange: (d: Date | undefined) => void;
  notes?: string;
  onNotesChange?: (n: string) => void;
  showQuantityPresets?: boolean;
  showQuantity?: boolean;
}

export function InstanceFieldsSection({
  quantity,
  onQuantityChange,
  purchaseDate,
  onPurchaseDateChange,
  expiryDate,
  onExpiryDateChange,
  notes,
  onNotesChange,
  showQuantityPresets = true,
  showQuantity = true,
}: InstanceFieldsSectionProps) {
  const { t } = useLingui();
  const [showPurchaseCalendar, setShowPurchaseCalendar] = useState(false);
  const [showExpiryCalendar, setShowExpiryCalendar] = useState(false);
  const [purchaseText, setPurchaseText] = useState('');
  const [expiryText, setExpiryText] = useState('');
  const [purchaseTextError, setPurchaseTextError] = useState(false);
  const [expiryTextError, setExpiryTextError] = useState(false);
  const purchaseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const expiryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (purchaseTimerRef.current) clearTimeout(purchaseTimerRef.current);
      if (expiryTimerRef.current) clearTimeout(expiryTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (purchaseTimerRef.current) {
      clearTimeout(purchaseTimerRef.current);
      purchaseTimerRef.current = null;
    }
    setPurchaseText(purchaseDate ? format(purchaseDate, 'yyyy/MM/dd') : '');
    setPurchaseTextError(false);
  }, [purchaseDate]);

  useEffect(() => {
    if (expiryTimerRef.current) {
      clearTimeout(expiryTimerRef.current);
      expiryTimerRef.current = null;
    }
    setExpiryText(expiryDate ? format(expiryDate, 'yyyy/MM/dd') : '');
    setExpiryTextError(false);
  }, [expiryDate]);

  const handlePurchaseTextCommit = () => {
    if (!purchaseText.trim()) {
      onPurchaseDateChange(undefined);
      return;
    }
    const parsed = parseDateInput(purchaseText);
    if (parsed) {
      if (purchaseTimerRef.current) clearTimeout(purchaseTimerRef.current);
      onPurchaseDateChange(parsed);
      setPurchaseTextError(false);
    } else {
      setPurchaseTextError(true);
      if (purchaseTimerRef.current) clearTimeout(purchaseTimerRef.current);
      purchaseTimerRef.current = setTimeout(() => {
        setPurchaseText(purchaseDate ? format(purchaseDate, 'yyyy/MM/dd') : '');
        setPurchaseTextError(false);
      }, 1500);
    }
  };

  const handleExpiryTextCommit = () => {
    if (!expiryText.trim()) {
      onExpiryDateChange(undefined);
      return;
    }
    const parsed = parseDateInput(expiryText);
    if (parsed) {
      if (expiryTimerRef.current) clearTimeout(expiryTimerRef.current);
      onExpiryDateChange(parsed);
      setExpiryTextError(false);
    } else {
      setExpiryTextError(true);
      if (expiryTimerRef.current) clearTimeout(expiryTimerRef.current);
      expiryTimerRef.current = setTimeout(() => {
        setExpiryText(expiryDate ? format(expiryDate, 'yyyy/MM/dd') : '');
        setExpiryTextError(false);
      }, 1500);
    }
  };

  const handleExpiryPreset = (days: number) => {
    onExpiryDateChange(parseLocalDate(calculateExpiryDate(days)));
  };

  return (
    <div className="space-y-5">
      {/* Quantity */}
      {showQuantity && (
        <div className="space-y-2">
          <Label htmlFor="instance-quantity">
            <Trans>Quantity</Trans>
          </Label>
          <div className="flex gap-2">
            <Input
              id="instance-quantity"
              type="number"
              min="1"
              className="flex-1"
              value={quantity}
              onChange={(e) => onQuantityChange(parseInt(e.target.value) || 1)}
            />
            {showQuantityPresets && (
              <div className="flex gap-1">
                {QUANTITY_PRESETS.map((n) => (
                  <Button
                    key={n}
                    type="button"
                    variant={quantity === n ? 'default' : 'outline'}
                    size="sm"
                    className="w-9 h-10"
                    onClick={() => onQuantityChange(n)}
                  >
                    {n}
                  </Button>
                ))}
              </div>
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            <Trans>Items with the same expiry date</Trans>
          </p>
        </div>
      )}

      {/* Purchase Date */}
      <div className="space-y-2">
        <Label>
          <Trans>Purchase Date</Trans>
        </Label>
        <div className="flex gap-2">
          <Input
            type="text"
            placeholder={t`YYYY/MM/DD`}
            value={purchaseText}
            onChange={(e) => {
              setPurchaseText(e.target.value);
              if (purchaseTimerRef.current) {
                clearTimeout(purchaseTimerRef.current);
                purchaseTimerRef.current = null;
              }
              setPurchaseTextError(false);
            }}
            onBlur={handlePurchaseTextCommit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handlePurchaseTextCommit();
              }
            }}
            className={cn('flex-1 h-10', purchaseTextError && 'border-destructive')}
          />
          <Popover open={showPurchaseCalendar} onOpenChange={setShowPurchaseCalendar}>
            <PopoverTrigger asChild>
              <Button type="button" variant="outline" size="icon" className="h-10 w-10 shrink-0">
                <CalendarIcon className="h-4 w-4" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={purchaseDate}
                onSelect={(date) => {
                  onPurchaseDateChange(date);
                  setShowPurchaseCalendar(false);
                }}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {/* Expiry Date */}
      <div className="space-y-2" data-tour="inventory-form-expiry">
        <Label>
          <Trans>Expiry Date</Trans>
        </Label>
        <div className="flex flex-wrap gap-2 mb-2">
          {EXPIRY_PRESETS.map((preset) => (
            <Button
              key={preset.days}
              type="button"
              variant={
                expiryDate && format(expiryDate, 'yyyy-MM-dd') === calculateExpiryDate(preset.days)
                  ? 'default'
                  : 'outline'
              }
              size="sm"
              onClick={() => handleExpiryPreset(preset.days)}
            >
              {preset.label}
            </Button>
          ))}
        </div>
        <div className="flex gap-2">
          <Input
            type="text"
            placeholder={t`YYYY/MM/DD`}
            value={expiryText}
            onChange={(e) => {
              setExpiryText(e.target.value);
              if (expiryTimerRef.current) {
                clearTimeout(expiryTimerRef.current);
                expiryTimerRef.current = null;
              }
              setExpiryTextError(false);
            }}
            onBlur={handleExpiryTextCommit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleExpiryTextCommit();
              }
            }}
            className={cn('flex-1 h-10', expiryTextError && 'border-destructive')}
          />
          <Popover open={showExpiryCalendar} onOpenChange={setShowExpiryCalendar}>
            <PopoverTrigger asChild>
              <Button type="button" variant="outline" size="icon" className="h-10 w-10 shrink-0">
                <CalendarIcon className="h-4 w-4" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={expiryDate}
                onSelect={(date) => {
                  onExpiryDateChange(date);
                  setShowExpiryCalendar(false);
                }}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {/* Notes */}
      {onNotesChange && (
        <div className="space-y-2">
          <Label htmlFor="instance-notes">
            <Trans>Notes</Trans>
          </Label>
          <Textarea
            id="instance-notes"
            value={notes ?? ''}
            onChange={(e) => onNotesChange(e.target.value)}
            placeholder={t`Optional notes about this batch`}
            rows={3}
          />
        </div>
      )}
    </div>
  );
}
